import React, { useState } from 'react';
import { FaFileDownload, FaUtensils, FaDumbbell, FaSpinner } from 'react-icons/fa';
import { dashboardAPI } from '../../services/dashboardAPI';
import { toast } from 'react-toastify';

const PlanDocuments = ({ documents = [] }) => {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (document) => {
    setDownloading(document.id);
    try {
      const { url, fileName } = await dashboardAPI.downloadDocument(document.id);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = fileName || document.fileName || 'document.pdf';
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success('Download started!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const getDocumentIcon = (documentType) => {
    switch (documentType) {
      case 'MEAL_PLAN':
        return <FaUtensils className="text-xl text-success-600" />;
      case 'WORKOUT_PLAN':
        return <FaDumbbell className="text-xl text-primary-600" />;
      default:
        return <FaFileDownload className="text-xl text-gray-600" />;
    }
  };

  const getDocumentLabel = (documentType) => {
    switch (documentType) {
      case 'MEAL_PLAN':
        return 'Meal Plan';
      case 'WORKOUT_PLAN':
        return 'Workout Plan';
      default:
        return 'Document';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
          <FaFileDownload className="text-xl text-success-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">Your Plans</h3>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-xl">
          <FaFileDownload className="text-4xl text-gray-300 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-700 mb-2">No Plans Available</h4>
          <p className="text-gray-500 text-sm">
            Your personalized meal and workout plans will appear here once they're ready.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => handleDownload(doc)}
              disabled={downloading === doc.id}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  {getDocumentIcon(doc.documentType)}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{getDocumentLabel(doc.documentType)}</p>
                  <p className="text-xs text-gray-500">
                    PDF {doc.fileSize && `• ${formatFileSize(doc.fileSize)}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-primary-600">
                {downloading === doc.id ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaFileDownload />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlanDocuments;
